import { endJourney, trackError } from '@/lib/telemetry'

// Helpers for the bootstrap "Something went wrong" surface (AppErrorScreen).
// Kept free of React so the error boundary and the oidc provider can both use
// them without import cycles.

const MAX_BODY_CHARS = 2048
const MAX_STACK_LINES = 8

// The diagnostic string shown behind the AppErrorScreen title click and fed to
// the copy button - one formatter so the two never drift. Redaction rules:
// pathname ONLY (the /callback query string carries the auth code), response
// body capped, nothing else appended from the error beyond name/message/stack.
export function formatErrorDetail(error: unknown, stage: string): string {
  const lines: string[] = []
  if (error instanceof Error) {
    lines.push(`${error.name}: ${error.message}`)
  } else if (error !== undefined && error !== null) {
    lines.push(String(error))
  } else {
    lines.push('(no captured error)')
  }
  lines.push(`stage: ${stage}`)
  const status = (error as { status?: number } | null)?.status
  if (status !== undefined) lines.push(`status: ${status}`)
  const body = (error as { body?: unknown } | null)?.body
  if (body !== undefined) {
    try {
      lines.push(`body: ${JSON.stringify(body).slice(0, MAX_BODY_CHARS)}`)
    } catch {
      /* circular body - skip */
    }
  }
  lines.push(`path: ${window.location.pathname}`)
  lines.push(`time: ${new Date().toISOString()}`)
  lines.push(`ua: ${navigator.userAgent}`)
  if (error instanceof Error && error.stack) {
    lines.push('', ...error.stack.split('\n').slice(0, MAX_STACK_LINES))
  }
  return lines.join('\n')
}

// The last sign-in failure, persisted across the signinRedirect round-trip so
// the attempt-breaker screen (which never holds an error object - the failure
// happened one redirect ago) can still show what broke. sessionStorage is
// per-tab; entries older than 15 minutes are treated as stale and ignored.
const LAST_AUTH_ERROR_KEY = 'lastAuthError'
const LAST_AUTH_ERROR_TTL_MS = 15 * 60 * 1000

export function persistLastAuthError(error: unknown, stage: string): void {
  try {
    sessionStorage.setItem(
      LAST_AUTH_ERROR_KEY,
      JSON.stringify({ t: Date.now(), detail: formatErrorDetail(error, stage) }),
    )
  } catch {
    /* storage unavailable (Safari private mode) - detail is lost, screen still shows */
  }
}

export function readLastAuthError(): string | null {
  try {
    const raw = sessionStorage.getItem(LAST_AUTH_ERROR_KEY)
    if (!raw) return null
    const { t, detail } = JSON.parse(raw) as { t?: number; detail?: string }
    if (typeof detail !== 'string' || typeof t !== 'number') return null
    if (Date.now() - t > LAST_AUTH_ERROR_TTL_MS) return null
    return detail
  } catch {
    return null
  }
}

export function clearLastAuthError(): void {
  try {
    sessionStorage.removeItem(LAST_AUTH_ERROR_KEY)
  } catch {
    /* storage unavailable */
  }
}

// Anonymous telemetry so a bootstrap break is visible in the collector instead of only via user
// reports (the issuer 404 was invisible until reported). The OTEL client ships to a separate host and
// swallows failures, so this works even when the worker/IdP are down.
//
// THE /callback GAP IS CLOSED. This used to call trackEvent, which is suppressed on /callback by the
// code-leak guard - and the comment here called signin-callback errors "an accepted gap". That is the
// single most common place auth breaks, and it blank-screened production invisibly.
//
// trackError emits from /callback deliberately, carrying ONLY the closed stage enum, the error's name,
// and the opaque session/device/journey ids. It reads nothing from `location`: no app.path, no query,
// no hash, no message - so the OIDC ?code still cannot escape. An ERROR-severity log plus an exception
// span event, and never a stack trace (minified SPA stacks have no diagnostic value without sourcemaps
// and are pure volume - §13).
export function reportBootstrapError(stage: string, err?: unknown): void {
  trackError(stage, err, { fromExcludedPath: true })
  // The authentication journey fails here. Clearing it stops a dead journey being resumed by the next
  // sign-in attempt 20 minutes later and stitching two unrelated attempts into one query result (§7).
  if (stage !== 'signin-loop') endJourney('authentication')
}
